import { ResponseError } from '../api-contracts';
import { ApiFactory } from '../services/api';
import { DataTypesEnum } from '../data/enums';
import { ObservationModel } from '../data/types';
import { ApiHelpers } from './apiHelpers';
import { MissingPhotoError } from './sendFailureHelpers';
import { ImageUtil } from '../utils/image.util';

interface UnsyncedImageModel extends ObservationModel {
  hasImage: boolean;
}

// App-only cache of small grid thumbnails, keyed by photoId. Full images stay
// in the 'photos' cache; thumbnails are never uploaded to the server.
const THUMBS_CACHE = 'photo-thumbs';

const observationImagesHelpers = {
  /**
   * Returns the full image blob for a photo: from the 'photos' cache if present,
   * otherwise downloaded from the API (and cached). Returns null when neither is
   * available (e.g. not cached and no observation id to fetch with).
   */
  getFullImageBlob: async (
    photoId: string,
    observationId?: number,
  ): Promise<Blob | null> => {
    const cache = await caches.open('photos');
    const cached = await cache.match(photoId);
    if (cached) {
      return cached.blob();
    }

    // 0 is an unsynced local observation (no server id yet) — don't try to fetch it.
    if (!observationId) {
      return null;
    }

    const api = ApiFactory.getInspectionApi();
    let response;
    try {
      response = await api.downloadFileRaw({ observationId });
    } catch (error) {
      if (error instanceof ResponseError) return null;
      throw error;
    }

    const blob = await response.raw.blob();
    if (!blob) {
      return null;
    }

    await cache.put(photoId, new Response(blob));
    return blob;
  },

  /**
   * Get-or-create the grid thumbnail for a photo and return an object URL for it.
   * Checks the thumbnail cache first so the full image is only loaded (via the
   * provider) when a thumbnail must be generated. The caller owns the returned
   * URL and should revoke it. Returns null if no source image is available.
   */
  getThumbUrl: async (
    photoId: string,
    fullBlobProvider: () => Promise<Blob | null>,
  ): Promise<string | null> => {
    const cache = await caches.open(THUMBS_CACHE);
    const existing = await cache.match(photoId);
    if (existing) {
      return URL.createObjectURL(await existing.blob());
    }

    const fullBlob = await fullBlobProvider();
    if (!fullBlob) {
      return null;
    }

    const thumb = await ImageUtil.createThumbnail(fullBlob);
    await cache.put(photoId, new Response(thumb));
    return URL.createObjectURL(thumb);
  },

  deleteThumb: async (photoId: string): Promise<void> => {
    const cache = await caches.open(THUMBS_CACHE);
    await cache.delete(photoId);
  },
  getUnsyncedImageObservations: async (
    observations: ObservationModel[],
  ): Promise<UnsyncedImageModel[]> => {
    const imageObservations = observations
      .filter(
        (observation) =>
          observation.observableItem?.dataTypeId === DataTypesEnum.image,
      )
      .map((obs) => ({ ...obs, hasImage: false }));

    // If no image observations, return empty array
    if (imageObservations.length === 0) return [];

    // Filter those that have not been synced or have not been processed and have an image in cache
    const unsyncedImages = await Promise.all([
      ...imageObservations.map(async (ob) => {
        // If already processed, then we know it is synced
        if (ob.fileReceived) return { ...ob, hasImage: true };

        // If it is not processed, check if we have the image in cache
        const imageExistsInCache =
          await observationImagesHelpers.imageExistsFromCache(
            ob.value?.split('/').pop()?.replace('[', '').replace(']', '') || '',
          );

        return { ...ob, hasImage: imageExistsInCache };
      }),
    ]);

    return unsyncedImages.filter(
      (ob) => ob.isSynced === false || ob.fileReceived === false,
    );
  },

  async getImageFromCache(photoName: string): Promise<Blob> {
    const cache = await caches.open('photos');
    const keys = await cache.keys();
    const url = keys.find((key) => key.url.endsWith(photoName));
    if (!url) {
      throw new Error(`Photo not found in cache for ID: ${photoName}`);
    }

    const response = await cache.match(url.url);
    if (!response) {
      throw new Error(`Cached photo not available for: ${photoName}`);
    }

    return response.blob();
  },

  downloadImageToCache: async (
    observation: ObservationModel,
  ): Promise<string | undefined> => {
    const api = ApiFactory.getInspectionApi();

    try {
      const response = await api.downloadFileRaw({
        observationId: observation.id,
      });

      const blob = await response.raw.blob();

      if (!blob) {
        return undefined;
      }

      // Store in cache
      const cache = await caches.open('photos');
      await cache.put(observation.value, new Response(blob));

      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      if (error instanceof ResponseError) return undefined;
      console.error('Error downloading image to cache:', error);
      return undefined;
    }
  },

  imageExistsFromCache: async (photoName: string): Promise<boolean> => {
    const cache = await caches.open('photos');
    const keys = await cache.keys();
    const url = keys.find((key) => key.url.endsWith(photoName));
    return !!url;
  },

  convertAndUpdateHEICImageInCache: async (
    observation: ObservationModel,
  ): Promise<boolean> => {
    const photoId = observation.value
      ?.split('/')
      .pop()
      ?.replace('[', '')
      .replace(']', '');
    if (!photoId) throw new Error('Invalid photo ID');

    const cache = await caches.open('photos');
    const keys = await cache.keys();
    const url = keys.find((key) => key.url.endsWith(photoId));
    if (!url) throw new MissingPhotoError(photoId);

    const response = await cache.match(url.url);
    if (!response) throw new MissingPhotoError(photoId);

    const blob = await response.blob();

    // Convert to JPEG
    const file = new File([blob], photoId, { type: blob.type });
    const isHEIC = await ImageUtil.isHEICImage(file);
    if (!isHEIC) return true; // Not HEIC, no conversion needed
    try {
      const jpegBlob = await ImageUtil.convertHEICToJPEGBlob(file);
      const newFilename = photoId.replace(/\.[^.]+$/, '.jpeg');

      // Update Observation value
      observation.value = observation.value?.replace(photoId, newFilename);

      // Delete old HEIC from cache storage - photos bucket
      await cache.delete(url.url);

      const newUrl = new URL(url.url.replace(photoId, newFilename));

      await cache.put(newUrl.toString(), new Response(jpegBlob));

      return true;
    } catch (error) {
      console.error('Error converting HEIC image:', error);
      return false;
    }
  },

  uploadObservationImage: async (
    observation: ObservationModel,
    onByteProgress?: (progress: { loaded: number; total: number }) => void,
  ): Promise<void> => {
    if (observation.observableItem.dataTypeId !== DataTypesEnum.image) return; // not an image
    const photoId = observation.value
      ?.split('/')
      .pop()
      ?.replace('[', '')
      .replace(']', '');
    if (!photoId) throw new Error('Invalid photo ID');

    const cache = await caches.open('photos');
    const keys = await cache.keys();
    const url = keys.find((key) => key.url.endsWith(photoId));
    if (!url) throw new MissingPhotoError(photoId);

    const response = await cache.match(url.url);
    if (!response) throw new MissingPhotoError(photoId);

    const blob = await response.blob();
    const endpoint =
      import.meta.env.VITE_API_URL + '/inspection/upload/' + observation.id;

    await new Promise<void>((resolve, reject) => {
      try {
        ApiHelpers.uploadImage({
          blob: blob,
          filename: photoId,
          endpoint,
          token: localStorage.getItem('jwtToken') as string,
          onProgress(loaded, total) {
            onByteProgress?.({ loaded, total });
          },
          onUploadComplete(response) {
            if (response.status !== 200) {
              reject(new Error(`Server error: ${response.statusText}`));
            } else {
              resolve();
            }
          },
          onError(error) {
            reject(error);
          },
        });
      } catch (error) {
        reject(error);
      }
    });
  },
};

export default observationImagesHelpers;
